import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { CaseType } from "../../types/Case";
import api from "../../utils/api";

interface UserCaseState {
  cases: CaseType[];
  selectedCase: CaseType | null;
  isLoading: boolean;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  dailyCases: number;
}

const initialState: UserCaseState = {
  cases: [],
  selectedCase: null,
  isLoading: false,
  totalCount: 0,
  currentPage: 1,
  totalPages: 1,
  dailyCases: 0,
};

// ===== Thunks =====
export const uploadCase = createAsyncThunk(
  "userCases/uploadCase",
  async (caseData: Partial<CaseType>, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/auth/user/add`, caseData);
      return data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to upload case"
      );
    }
  }
);

export const fetchUserCases = createAsyncThunk(
  "userCases/fetchUserCases",
  async (
    {
      user_id,
      page = 1,
      limit = 50,
    }: { user_id: number; page?: number; limit?: number },
    { rejectWithValue }
  ) => {
    try {
      const { data } = await api.get(
        `/auth/user-cases/${user_id}`,
        { params: { page, limit } }
      );
      return data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch cases"
      );
    }
  }
);

export const fetchCaseById = createAsyncThunk(
  "userCases/fetchCaseById",
  async (case_id: string, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/auth/case/${case_id}`
      );
      return data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.error || "Failed to fetch case detail"
      );
    }
  }
);

// ===== Slice =====
const userCaseSlice = createSlice({
  name: "userCases",
  initialState,
  reducers: {
    clearSelectedCase: (state) => {
      state.selectedCase = null;
    },
  },
  extraReducers: (builder) => {
    // Upload case
    builder
      .addCase(uploadCase.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(uploadCase.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cases.unshift(action.payload);
      })
      .addCase(uploadCase.rejected, (state) => {
        state.isLoading = false;
      })

      // Fetch user all cases
      .addCase(fetchUserCases.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchUserCases.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cases = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.currentPage = action.payload.currentPage;
        state.totalPages = action.payload.totalPages;
        state.dailyCases = Number(action.payload.dailyCases) || 0;
      })
      .addCase(fetchUserCases.rejected, (state) => {
        state.isLoading = false;
      })

      // Fetch case by ID
      .addCase(fetchCaseById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCaseById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedCase = action.payload;
      })
      .addCase(fetchCaseById.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export const { clearSelectedCase } = userCaseSlice.actions;
export default userCaseSlice.reducer;